import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="turnstile"
// Supports both visible and invisible (managed) modes
// For invisible mode, set data-turnstile-size-value="invisible"
export default class extends Controller {
  static targets = ["widget", "token"]
  static values = {
    siteKey: String,
    theme: { type: String, default: "light" },
    size: { type: String, default: "normal" },  // "normal", "compact", or "invisible"
    action: { type: String, default: "" },       // Optional action name for analytics
    retry: { type: String, default: "auto" }     // "auto" or "never"
  }

  connect() {
    this.formIsValid = false // Initialize as invalid until form validation runs
    this.isInvisible = this.sizeValue === "invisible"

    // In development/testing, set a dummy token immediately
    if (this.hasTokenTarget && (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1'))) {
      this.tokenTarget.value = "XXXX.DUMMY.TOKEN.XXXX"
      // Simulate successful verification after a brief delay
      setTimeout(() => {
        this.onSuccess("XXXX.DUMMY.TOKEN.XXXX")
      }, 100)
    } else {
      this.loadTurnstile()
    }

    // Only set up form validation listener for visible mode
    if (!this.isInvisible) {
      this.setupFormValidationListener()
    }
  }

  disconnect() {
    if (this.widgetId) {
      window.turnstile?.remove(this.widgetId)
    }
  }

  loadTurnstile() {
    // Check if Turnstile is already loaded
    if (window.turnstile) {
      this.renderWidget()
      return
    }

    // Load Turnstile script
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
    script.async = true
    script.defer = true
    script.onload = () => this.renderWidget()
    script.onerror = () => this.handleError("Failed to load Turnstile")

    document.head.appendChild(script)
  }

  renderWidget() {
    if (!this.siteKeyValue || !window.turnstile) {
      this.handleError("Turnstile configuration missing")
      return
    }

    try {
      const options = {
        sitekey: this.siteKeyValue,
        theme: this.themeValue,
        retry: this.retryValue,
        callback: (token) => this.onSuccess(token),
        "error-callback": () => this.onError(),
        "expired-callback": () => this.onExpired(),
        "timeout-callback": () => this.onTimeout()
      }

      // Size only applies to Managed/Non-Interactive; Invisible widgets ignore it. API accepts normal|flexible|compact only.
      if (this.sizeValue && this.sizeValue !== "invisible") {
        options.size = this.sizeValue
      }

      // Add action if specified (for analytics)
      if (this.actionValue) {
        options.action = this.actionValue
      }

      this.widgetId = window.turnstile.render(this.widgetTarget, options)
    } catch (error) {
      this.handleError(`Failed to render Turnstile: ${error.message}`)
    }
  }

  onSuccess(token) {
    // console.log("✅ Turnstile token received:", token)
    this.tokenTarget.value = token
    // Only manage button state for visible mode
    if (!this.isInvisible) {
      this.enableSubmission()
    }
  }

  onError() {
    console.error("Turnstile verification error")
    this.tokenTarget.value = ""
    // In invisible mode, silently retry - don't block user
    if (this.isInvisible) {
      // Turnstile will auto-retry based on retry setting
      return
    }
    this.disableSubmission()
    this.showUserError("Security verification failed. Please try again.")
  }

  onExpired() {
    // console.warn("Turnstile token expired")
    this.tokenTarget.value = ""
    // In invisible mode, silently refresh token
    if (this.isInvisible) {
      this.reset()
      return
    }
    this.disableSubmission()
    this.showUserError("Security verification expired. Please verify again.")
  }

  onTimeout() {
    // console.warn("Turnstile verification timeout")
    this.tokenTarget.value = ""
    // In invisible mode, allow submission anyway to avoid blocking users
    if (this.isInvisible) {
      return
    }
    this.disableSubmission()
    this.showUserError("Security verification timed out. Please try again.")
  }

  get submitButton() {
    // Find the submit button in the form
    const form = this.element.closest('form')
    return form ? form.querySelector('input[type="submit"], button[type="submit"]') : null
  }

  enableSubmission() {
    const button = this.submitButton
    if (button) {
      // Only enable if form is also valid (if form validator is present)
      const shouldEnable = this.isFormValid()

      button.disabled = !shouldEnable
      if (shouldEnable) {
        button.classList.remove('opacity-50', 'cursor-not-allowed')
      } else {
        button.classList.add('opacity-50', 'cursor-not-allowed')
      }
    }
  }

  disableSubmission() {
    const button = this.submitButton
    if (button) {
      button.disabled = true
      button.classList.add('opacity-50', 'cursor-not-allowed')
    }
  }

  showUserError(message) {
    // Create or update error message
    let errorDiv = this.element.querySelector('.turnstile-error')
    if (!errorDiv) {
      errorDiv = document.createElement('div')
      errorDiv.className = 'turnstile-error text-sm text-red-600 mt-2 p-2 bg-red-50 border border-red-200 rounded'
      this.widgetTarget.appendChild(errorDiv)
    }
    errorDiv.textContent = message
    errorDiv.classList.remove('hidden')
  }

  hideUserError() {
    const errorDiv = this.element.querySelector('.turnstile-error')
    if (errorDiv) {
      errorDiv.classList.add('hidden')
    }
  }

  handleError(message) {
    console.error(message)
    // In case of configuration errors, allow form submission to prevent lockout
    this.enableSubmission()
  }

  setupFormValidationListener() {
    // Listen for form validation changes from form-validator controller
    const form = this.element.closest('form')
    if (!form) return

    // Listen for both possible event names (Stimulus adds controller prefix)
    form.addEventListener("form-validator:form-valid", (event) => {
      this.formIsValid = event.detail.isValid

      // Re-evaluate submission state when form validity changes
      if (this.tokenTarget.value) {
        this.enableSubmission()
      } else {
        this.disableSubmission()
      }
    })

    // Also listen for validation-changed events
    form.addEventListener("form-validator:validation-changed", (event) => {
      this.formIsValid = event.detail.isValid

      // Re-evaluate submission state when form validity changes
      if (this.tokenTarget.value) {
        this.enableSubmission()
      } else {
        this.disableSubmission()
      }
    })
  }

  isFormValid() {
    // First check if form validator has set our validity state
    if (this.hasOwnProperty('formIsValid')) {
      return this.formIsValid
    }

    // Fallback: check if form fields appear filled
    const form = this.element.closest('form')
    if (!form) return true

    const requiredFields = form.querySelectorAll('[required]')
    return Array.from(requiredFields).every(field => {
      if (field.type === 'email') {
        return field.value.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value)
      }
      if (field.type === 'password') {
        return field.value.length >= 8
      }
      return field.value.trim() !== ''
    })
  }

  // Reset widget programmatically
  reset() {
    if (this.widgetId && window.turnstile) {
      window.turnstile.reset(this.widgetId)
      this.tokenTarget.value = ""
      this.disableSubmission()
      this.hideUserError()
    }
  }
}
